import template from './search.html';
import { get } from 'lodash-es';
import {
  ITEM_KINDS,
  STATES,
  ITEM_DEFINITION_CODES,
} from '../../../../constants';
import { trackEngagement } from '@mover-app/engagement';
import { actions, selectors } from '../../../../state';
import { CATEGORIES } from '../categories';
import './search.global.css';
import '@updater-components/dialog';

const MESSAGES = {
  SUCCESS: `We're now submitting your updates to each organization. Some updates may take longer than others, but you can always check the status here.`,
  ERROR: `Sorry, we couldn't update your accounts. Please try again later.`,
};

class UpdateAccountsSearchController {
  constructor(updStore, $element, $scope) {
    'ngInject';
    Object.assign(this, {
      updStore,
      $element,
      $scope,
      organizations: [],
    });
  }

  $onInit() {
    this.disconnect = this.updStore.connect(mapStateToThis)(this);
  }

  $onDestroy() {
    this.disconnect();
    this.typeahead.removeEventListener('change', this._onChangeTypeahead);
  }

  $postLink() {
    this.typeahead = this.$element.find('upd-typeahead')[0];
    this.modal = this.$element.find('upd-dialog')[0];
    this.typeahead.options = [];
    this.typeahead.addEventListener('change', this._onChangeTypeahead);
  }

  get hasOrganizations() {
    return !!this.organizations.length;
  }

  backToCategories() {
    this.updStore.dispatch(actions.router.stateGo(STATES.STEP_UPDATE_ACCOUNTS));
  }

  _onChangeTypeahead = e => {
    if (e.target !== this.typeahead) return;

    if (e.detail) {
      const organization = e.detail.item;
      this.$scope.$apply(() =>
        organizationHasIdentifiers(organization)
          ? this._openModal(organization)
          : this.addOrganization(organization)
      );
    }
  };

  _openModal(organization) {
    this.modalOrganization = organization;
    this.modalIdentifiers = organization.identifiers;
    this.modal.showModal();
  }

  _closeModal(identifiers) {
    this.addOrganization({
      ...this.modalOrganization,
      identifiers,
    });
    this.modal.close();
  }

  _dismissModal() {
    this._resetForm.bind(this);
    this.modal.close();
  }

  addOrganization(organization) {
    this.organizations = [...this.organizations, organization];
    this._resetForm();
  }

  _resetForm() {
    this.search = null;
    this._setFormPristine();
  }

  _setFormPristine() {
    this.form.$setPristine();
  }

  onSubmit(organizations) {
    const { dispatch } = this.updStore;
    const analyticsProperties = {
      Accounts: organizations.length,
    };

    trackEngagement({
      name: 'Update Accounts',
      code: ITEM_DEFINITION_CODES.UPDATE_ACCOUNT,
      trigger: 'form',
    });

    const success = () => {
      // REVIEW: Manually checking for now. We can do this via
      // angular-roles, but would have to make another call to
      // get the updated move.
      if (this.isMoveVerified) {
        dispatch(actions.router.stateGo(STATES.STEP_UPDATE_ACCOUNTS));
        dispatch(actions.message.success(MESSAGES.SUCCESS));
      } else {
        dispatch(actions.router.stateGo(STATES.STEP_UPDATE_ACCOUNTS_VERIFY));
      }
      dispatch(
        actions.analytics.track('Updated Accounts', analyticsProperties)
      );
    };

    const error = response => {
      dispatch(actions.message.error(MESSAGES.ERROR));
      dispatch(
        actions.analytics.track('Tried to Update Accounts', {
          ...analyticsProperties,
          response,
        })
      );
    };

    const data = organizations.map(org => ({
      organization_id: org.id,
      identifiers: org.identifiers,
      name_id: this.nameId,
      move_id: this.moveId,
      kind: ITEM_KINDS.UPDATE_ACCOUNT,
    }));

    dispatch(actions.bulk.items({ data })).then(success, error);
  }

  searchOrganizations(fulltext) {
    const success = result => {
      const results = get(result, 'payload.data')
        .map(mapSearchOrganization)
        // Don't show orgs that are already move items
        .filter(org => !this.itemsOrganizationIds.includes(org.id))
        // Dont show orgs that the user already added
        .filter(org => !this.organizations.map(org => org.id).includes(org.id));

      this.noResults = !results.length;
      this.typeahead.options = results;
      return results;
    };

    const error = () => {
      this.noResults = true;
    };

    const filter = {
      categories: this.category.categories,
      fulltext,
    };

    return this.updStore
      .dispatch(actions.organization.search({ filter }))
      .then(success, error);
  }

  removeOrganization(organization) {
    this.organizations = this.organizations.filter(
      org => org.id !== organization.id
    );

    // If the user just removed the last organization we reset the form so the
    // form validation doesn't appear until the next form submission
    if (!this.hasOrganizations) {
      this._setFormPristine();
    }
  }

  onChange() {
    this.noResults = false;
    if (this.search && this.search.length > 3) {
      this.searchOrganizations(this.search);
    }
  }
}

export const UpdateAccountsSearchComponent = {
  template,
  controller: UpdateAccountsSearchController,
};

function mapStateToThis(state) {
  const { current, pending, name, router } = selectors;
  const category = CATEGORIES.find(
    category => category.link === router.currentParamsProp('category', state)
  );
  const nameId = name.selectedId(state) || current.names(state)[0].id(state);
  const move = current.move(state);

  return {
    category,
    moveId: move.id(state),
    searching: pending.match(actions.organization.isRead, state),
    submitting: pending.match(actions.organization.isCreate, state),
    isMoveVerified: move.isVerified(state),
    nameId,
    itemsOrganizationIds: current
      .updateAccountItems(state)
      .filter(item => item.nameId(state) === nameId)
      // _id properties are returned as numbers, but the search results are returned as strings
      .map(item => String(item.organizationId(state))),
  };
}

function mapSearchOrganization(org) {
  return {
    id: org.id,
    name: org.attributes.name,
    identifiers: org.attributes.identifiers,
  };
}

function organizationHasIdentifiers(org) {
  return org.identifiers && !!org.identifiers.length;
}
